import React, { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import {
  CREATE_SHOP_CAREER_PORTAL,
  UPDATE_SHOP_CAREER_PORTAL,
  CREATE_TESTIMONIAL,
  UPDATE_TESTIMONIAL,
  DELETE_TESTIMONIAL,
  CREATE_DEPARTMENT,
  UPDATE_DEPARTMENT,
  DELETE_DEPARTMENT,
} from '../apollo/mutation';
import { GET_CAREER_PORTAL, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import {
  Form,
  Page,
  Card,
  Label,
  Layout,
  FormLayout,
  TextField,
  AppProvider,
  DropZone,
  Button,
  Frame,
  ContextualSaveBar,
  InlineError,
  Stack,
  Thumbnail,
  Caption,
  Checkbox,
  Tooltip,
  Subheading,
  Toast,
  Link,
  Modal,
  Icon,
  ResourceList,
  ResourceItem,
  TextStyle,
  Tabs,
} from '@shopify/polaris';
import { CirclePlusMinor, ViewMajorMonotone } from '@shopify/polaris-icons';
import { prop, isNil, isEmpty, path, compose, find, values, reject, propOr, equals, pathOr } from 'ramda';
import Loader from 'react-loader-spinner';

import { onSubmitFailed } from '../../utils/errors';

import { uploadImageUrl } from '../../utils/imagesUrl';

import ColorPickerCard from './ColorPickerCard';

const heroTabs = [
  {
    id: 'hero-content',
    content: 'Content',
    accessibilityLabel: 'Hero Content',
    panelID: 'hero-content',
  },
  {
    id: 'hero-styles',
    content: 'Styles',
    accessibilityLabel: 'Hero Styles',
    panelID: 'hero-styles',
  },
];

const introTabs = [
  {
    id: 'intro-content',
    content: 'Content',
    accessibilityLabel: 'Intro Content',
    panelID: 'intro-content',
  },
  {
    id: 'intro-styles',
    content: 'Styles',
    accessibilityLabel: 'Intro Styles',
    panelID: 'intro-styles',
  },
];

const employeeTabs = [
  {
    id: 'employee-content',
    content: 'Content',
    accessibilityLabel: 'Employee Content',
    panelID: 'employee-content',
  },
  {
    id: 'employee-styles',
    content: 'Styles',
    accessibilityLabel: 'Employee Styles',
    panelID: 'employee-styles',
  },
];

const jobListingTabs = [
  {
    id: 'job-listing-content',
    content: 'Content',
    accessibilityLabel: 'Job Listings Content',
    panelID: 'job-listing-content',
  },
  {
    id: 'job-listing-styles',
    content: 'Styles',
    accessibilityLabel: 'Job Listings Styles',
    panelID: 'job-listing-styles',
  },
];

const deiTabs = [
  {
    id: 'dei-content',
    content: 'Content',
    accessibilityLabel: 'Dei Content',
    panelID: 'dei-content',
  },
  {
    id: 'dei-styles',
    content: 'Styles',
    accessibilityLabel: 'Dei Styles',
    panelID: 'dei-styles',
  },
];

const departmentTabs = [
  {
    id: 'department-content',
    content: 'Content',
    accessibilityLabel: 'Department Content',
    panelID: 'department-content',
  },
  {
    id: 'department-styles',
    content: 'Styles',
    accessibilityLabel: 'Department Styles',
    panelID: 'department-styles',
  },
];

const defaultWhiteColor = {
  hue: 0,
  brightness: 1,
  saturation: 1,
};

const defaultGrayColor = {
  hue: 0,
  brightness: 0.227,
  saturation: 0,
};

const defaultDarkGrayColor = {
  hue: 0,
  brightness: 0.198,
  saturation: 0.0099,
};

const CareerPortal = ({ shopifyDomain, shopifyToken, history, careerPageLink }) => {
  const [pasted, setPasted] = useState(false);
  const togglePasted = useCallback(() => setPasted((pasted) => !pasted), []);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTestimonialModalOpen, setIsTestimonialModalOpen] = useState(false);
  const [isDepartmentsModalOpen, setIsDepartmentsModalOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [isConfirmDeleteDepartmentModalOpen, setIsConfirmDeleteDepartmentModalOpen] = useState(false);
  const [toastStatus, setToast] = useState({ isOpen: false, text: '' });
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const [selectedHeroTab, setSelectedHeroTab] = useState(0);
  const [selectedIntroTab, setSelectedIntroTab] = useState(0);
  const [selectedEmployeeTab, setSelectedEmployeeTab] = useState(0);
  const [selectedJobListingTab, setSelectedJobListingTab] = useState(0);
  const [selectedDeiTab, setSelectedDeiTab] = useState(0);
  const [selectedDepartmentTab, setSelectedDepartmentTab] = useState(0);

  const [isStylesHeroEnabled, setIsStylesHeroEnabled] = useState(false);
  const [isStylesIntroEnabled, setIsStylesIntroEnabled] = useState(false);
  const [isStylesEmployeeEnabled, setIsStylesEmployeeEnabled] = useState(false);
  const [isStylesJobListEnabled, setIsStylesJobListEnabled] = useState(false);
  const [isStylesDeiEnabled, setIsStylesDeiEnabled] = useState(false);
  const [isStylesDepartmentEnabled, setIsStylesDepartmentEnabled] = useState(false);

  const [backgroundHeroColor, setBackgroundHeroColor] = useState(defaultWhiteColor);
  const [headingHeroColor, setHeadingHeroColor] = useState(defaultGrayColor);
  const [subheadingHeroColor, setSubheadingHeroColor] = useState(defaultGrayColor);

  const [backgroundIntroColor, setBackgroundIntroColor] = useState(defaultWhiteColor);
  const [headingIntroColor, setHeadingIntroColor] = useState(defaultGrayColor);
  const [bodyIntroColor, setBodyIntroColor] = useState(defaultDarkGrayColor);

  const [backgroundEmployeeColor, setBackgroundEmployeeColor] = useState(defaultWhiteColor);
  const [employeeNameTextColor, setEmployeeNameTextColor] = useState(defaultDarkGrayColor);
  const [jobTitleEmployeeTextColor, setJobTitleEmployeeTextColor] = useState(defaultDarkGrayColor);
  const [quoteEmployeeTextColor, setQuoteEmployeeTextColor] = useState(defaultDarkGrayColor);

  const [jobListBackgroundColor, setJobListBackgroundColor] = useState(defaultWhiteColor);
  const [jobListTextColor, setJobListTextColor] = useState(defaultDarkGrayColor);
  const [jobListListColor, setJobListListColor] = useState(defaultWhiteColor);
  const [jobListHeadingColor, setJobListHeadingColor] = useState(defaultDarkGrayColor);
  const [jobListButtonColor, setJobListButtonColor] = useState(defaultWhiteColor);
  const [jobListButtonTextColor, setJobListButtonTextColor] = useState(defaultDarkGrayColor);

  const [backgroundDeiColor, setBackgroundDeiColor] = useState(defaultWhiteColor);
  const [headingDeiColor, setHeadingDeiColor] = useState(defaultGrayColor);
  const [bodyDeiColor, setBodyDeiColor] = useState(defaultDarkGrayColor);

  const [backgroundDepartmentColor, setBackgroundDepartmentColor] = useState(defaultWhiteColor);
  const [blockDepartmentColor, setBlockDepartmentColor] = useState(defaultWhiteColor);
  const [headingDepartmentColor, setHeadingDepartmentColor] = useState(defaultGrayColor);
  const [departmentNameTextColor, setDepartmentNameTextColor] = useState(defaultDarkGrayColor);
  const [departmentDescriptionTextColor, setDepartmentDescriptionTextColor] = useState(defaultDarkGrayColor);

  const [form, setState] = useState({
    heroHeader: 'View Our Open Roles',
    heroSubheader: "We're looking for great people to join our growing team",
    heroButtonText: '',
    heroRoles: false,
    pageTitle: 'Careers',
    metaDescription: "We're looking for great people to join our growing team",
    urlAndHandle: '',
    introductionParagraph: false,
    introductionBody: '',
    introductionHeading: '',
    deiStatment: false,
    deiStatmentBody:
      'We are committed to fostering, cultivating and preserving a culture of diversity and inclusion.  Our employees are the most valuable asset we have. The collective sum of the individual differences, life experiences, knowledge, inventiveness, innovation, self-expression, unique capabilities and talent that our employees invest in their work represents a significant part of not only our culture, but our reputation and company’s achievement as well.',
    departmentsSection: false,
    departments: [],
    employeeTestimonial: false,
    employeeTestimonialName: '',
    employeeTestimonialQuote: '',
    employeeTestimonialRole: '',
    testimonials: [],
    jobListHeadingText: '',
  });
  const [formErrors, setFormErrors] = useState({
    heroHeader: false,
    heroSubheader: false,
    heroRoles: false,
    heroButtonText: false,
    introductionBody: false,
    introductionHeading: false,
    deiStatmentBody: false,
    jobListHeadingText: false,
  });
  const [file, setFile] = useState();
  const [hasColorChanged, setHasColorChanged] = useState(false);

  const [createShopCareerPortal] = useMutation(CREATE_SHOP_CAREER_PORTAL);
  const [updateShopCareerPortal] = useMutation(UPDATE_SHOP_CAREER_PORTAL);
  const [createDepartment] = useMutation(CREATE_DEPARTMENT);
  const [updateDepartment] = useMutation(UPDATE_DEPARTMENT);
  const [createTestimonial] = useMutation(CREATE_TESTIMONIAL);
  const [updateTestimonial] = useMutation(UPDATE_TESTIMONIAL);
  const [deleteTestimonial] = useMutation(DELETE_TESTIMONIAL);
  const [deleteDepartment] = useMutation(DELETE_DEPARTMENT);

  const toggleIsHeroStylesEnabled = () => {
    setHasColorChanged(true);
    setIsStylesHeroEnabled(!isStylesHeroEnabled);
  };
  const toggleIsIntroStylesEnabled = () => {
    setHasColorChanged(true);
    setIsStylesIntroEnabled(!isStylesIntroEnabled);
  };
  const toggleIsJobListStylesEnabled = () => {
    setHasColorChanged(true);
    setIsStylesJobListEnabled(!isStylesJobListEnabled);
  };
  const toggleIsEmployeeStylesEnabled = () => {
    setHasColorChanged(true);
    setIsStylesEmployeeEnabled(!isStylesEmployeeEnabled);
  };
  const toggleIsDeiStylesEnabled = () => {
    setHasColorChanged(true);
    setIsStylesDeiEnabled(!isStylesDeiEnabled);
  };
  const toggleIsDepartmentStylesEnabled = () => {
    setHasColorChanged(true);
    setIsStylesDepartmentEnabled(!isStylesDepartmentEnabled);
  };

  const toggleIsTestimonialModalOpen = () => setIsTestimonialModalOpen(!isTestimonialModalOpen);
  const toggleIsDepartmentsModalOpen = () => setIsDepartmentsModalOpen(!isDepartmentsModalOpen);
  const toggleIsConfirmDeleteModalOpen = () => setIsConfirmDeleteModalOpen(!isConfirmDeleteModalOpen);
  const toggleIsConfirmDeleteDepartmentModalOpen = () =>
    setIsConfirmDeleteDepartmentModalOpen(!isConfirmDeleteDepartmentModalOpen);
  const toggleToast = (text) =>
    setToast((prev) => ({
      isOpen: !prev.isOpen,
      text: prev.isOpen ? prev.text : text,
    }));

  const { loading, data, refetch } = useQuery(GET_CAREER_PORTAL, {
    variables: { shopifyToken },
  });

  const { data: currentSubscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, {
    variables: { shopifyToken },
  });
  const shop = propOr({}, 'shop')(currentSubscriptionData);
  const currentSubscription = propOr({}, 'currentSubscription')(shop);
  const careerPortalSet = propOr(false, 'careerPortalSet')(shop);
  const subscriptionPlan = currentSubscription.subscriptionPlan;

  const isCareerPortalEmpty = !careerPortalSet;

  const changeRoute = prop('push')(history);

  useEffect(() => {
    if (!loading) {
      const careerPortalData = propOr({}, 'careerPortal')(data);
      if (!isEmpty(careerPortalData)) {
        setState({
          heroHeader: careerPortalData.headingText,
          heroSubheader: careerPortalData.subheadingText,
          heroRoles: careerPortalData.openRolesButton,
          heroButtonText: careerPortalData.buttonText,
          jobListHeadingText: careerPortalData.jobListHeadingText,
          ...careerPortalData,
          introductionParagraph: careerPortalData.introductionParagraph || false,
          deiStatment: careerPortalData.deiStatment || false,
          employeeTestimonial: careerPortalData.employeeTestimonial || false,
          departmentsSection: careerPortalData.departmentsSection || false,
          deiStatmentBody: careerPortalData.deiStatmentBody || form.deiStatmentBody,
          departments: careerPortalData.departments
            ? careerPortalData.departments.map((department) => ({
                id: department.id,
                departmentName: department.headingText,
                departmentDescription: department.subheadingText,
              }))
            : [],
        });
      }

      const styleControls = pathOr({}, ['careerPortal', 'styleControls'])(data);

      if (!isEmpty(styleControls)) {
        const heroSection = propOr({}, 'hero_section')(styleControls);
        setIsStylesHeroEnabled(heroSection.enabled);
        if (!isEmpty(heroSection.background_color) && heroSection.enabled) setBackgroundHeroColor(heroSection.background_color);
        if (!isEmpty(heroSection.heading_color) && heroSection.enabled) setHeadingHeroColor(heroSection.heading_color);
        if (!isEmpty(heroSection.subheading_color) && heroSection.enabled) setSubheadingHeroColor(heroSection.subheading_color);

        const introSection = propOr({}, 'paragraph_section')(styleControls);
        setIsStylesIntroEnabled(introSection.enabled);
        if (!isEmpty(introSection.background_color) && introSection.enabled)
          setBackgroundIntroColor(introSection.background_color);
        if (!isEmpty(introSection.heading_color) && introSection.enabled) setHeadingIntroColor(introSection.heading_color);
        if (!isEmpty(introSection.body_color) && introSection.enabled) setBodyIntroColor(introSection.body_color);

        const testimonialSection = propOr({}, 'testimonial_section')(styleControls);
        setIsStylesEmployeeEnabled(testimonialSection.enabled);
        if (!isEmpty(testimonialSection.background_color) && testimonialSection.enabled)
          setBackgroundEmployeeColor(testimonialSection.background_color);
        if (!isEmpty(testimonialSection.employee_color) && testimonialSection.enabled)
          setEmployeeNameTextColor(testimonialSection.employee_color);
        if (!isEmpty(testimonialSection.job_title_color) && testimonialSection.enabled)
          setJobTitleEmployeeTextColor(testimonialSection.job_title_color);
        if (!isEmpty(testimonialSection.quote_color) && testimonialSection.enabled)
          setQuoteEmployeeTextColor(testimonialSection.quote_color);

        const jobListSection = propOr({}, 'job_list_section')(styleControls);
        setIsStylesJobListEnabled(jobListSection.enabled);

        if (!isEmpty(jobListSection.background_color) && jobListSection.enabled)
          setJobListBackgroundColor(jobListSection.background_color);
        if (!isEmpty(jobListSection.heading_color) && jobListSection.enabled)
          setJobListHeadingColor(jobListSection.heading_color);
        if (!isEmpty(jobListSection.job_list_text_color) && jobListSection.enabled)
          setJobListTextColor(jobListSection.job_list_text_color);
        if (!isEmpty(jobListSection.apply_button_color) && jobListSection.enabled)
          setJobListButtonColor(jobListSection.apply_button_color);
        if (!isEmpty(jobListSection.apply_button_text_color) && jobListSection.enabled)
          setJobListButtonTextColor(jobListSection.apply_button_text_color);
        if (!isEmpty(jobListSection.job_list_color) && jobListSection.enabled) setJobListListColor(jobListSection.job_list_color);

        const deiSection = propOr({}, 'dei_section')(styleControls);
        setIsStylesDeiEnabled(deiSection.enabled);
        if (!isEmpty(deiSection.background_color) && deiSection.enabled) setBackgroundDeiColor(deiSection.background_color);
        if (!isEmpty(deiSection.heading_color) && deiSection.enabled) setHeadingDeiColor(deiSection.heading_color);
        if (!isEmpty(deiSection.body_color) && deiSection.enabled) setBodyDeiColor(deiSection.body_color);

        const departmentSection = propOr({}, 'department_section')(styleControls);
        setIsStylesDepartmentEnabled(departmentSection.enabled);
        if (!isEmpty(departmentSection.background_color) && departmentSection.enabled)
          setBackgroundDepartmentColor(departmentSection.background_color);
        if (!isEmpty(departmentSection.block_color) && departmentSection.enabled)
          setBlockDepartmentColor(departmentSection.block_color);
        if (!isEmpty(departmentSection.heading_color) && departmentSection.enabled)
          setHeadingDepartmentColor(departmentSection.heading_color);
        if (!isEmpty(departmentSection.department_color) && departmentSection.enabled)
          setDepartmentNameTextColor(departmentSection.department_color);
        if (!isEmpty(departmentSection.description_color) && departmentSection.enabled)
          setDepartmentDescriptionTextColor(departmentSection.description_color);
      }
    }
  }, [data]);

  const actionButtonText = isCareerPortalEmpty ? 'Save' : 'Update';
  const toggleIsDirty = useCallback(() => {
    setIsDirty((isDirty) => !isDirty);
    setHasColorChanged(false);
  }, []);

  const onActiveSaveBar = () => hasColorChanged || isDirty || !!file;

  const contextualSaveBarMarkup = (createShopCareerPortal) =>
    onActiveSaveBar() ? (
      <ContextualSaveBar
        message='Unsaved changes'
        saveAction={{
          onAction: (e) => {
            e.preventDefault();
            handleFormSubmit({ createShopCareerPortal, updateShopCareerPortal });
          },
        }}
        discardAction={{
          onAction: toggleIsDirty,
        }}
      />
    ) : null;

  const toastMarkup = toastStatus.isOpen ? <Toast content={toastStatus.text} onDismiss={toggleToast} /> : null;

  const updateField = (name) => (value) => {
    value && setIsDirty(true);

    let errors = { ...formErrors, [name]: false };

    let cleanButtonText = {};
    if (name === 'heroRoles' && !value) {
      cleanButtonText = {
        heroButtonText: '',
      };
      errors = {
        ...errors,
        heroButtonText: false,
      };
    }
    setFormErrors(errors);
    setState({
      ...form,
      [name]: value,
      ...cleanButtonText,
    });
  };

  const handleDropZoneDrop = useCallback((_dropFiles, acceptedFiles, _rejectedFiles) => setFile((file) => acceptedFiles[0]), []);

  const validImageTypes = ['image/gif', 'image/jpeg', 'image/png'];

  const fileUpload = !file && <DropZone.FileUpload actionTitle='Add photo' actionHint='or drop an file to upload' />;
  const uploadedFile = file && (
    <Stack>
      <Thumbnail
        size='small'
        alt={file.name}
        source={
          validImageTypes.indexOf(file.type) > 0
            ? window.URL.createObjectURL(file)
            : 'https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png?12678548500147524304'
        }
      />
      <div>
        {file.name} <Caption>{file.size} bytes</Caption>
      </div>
    </Stack>
  );

  const onSuccess = () => {
    setIsLoading(false);
    history.push('/job-list');
  };

  const onError = (e) => {
    let messages = path(['graphQLErrors', 0, 'message'])(e);
    toggleToast(`Error: ${messages}`);
    setIsLoading(false);
  };

  const isSubscribedUser = !isEmpty(currentSubscription);
  const isPremiumUser = isSubscribedUser && currentSubscription.subscriptionType === 'Premium';

  const handleFormSubmit = async ({ createShopCareerPortal, updateShopCareerPortal }) => {
    setIsLoading(true);
    const payload = {
      variables: {
        headingText: form.heroHeader,
        subheadingText: form.heroSubheader,
        urlAndHandle: form.urlAndHandle,
        metaDescription: form.metaDescription,
        pageTitle: form.pageTitle,
        deiStatment: form.deiStatment,
        deiStatmentBody: form.deiStatmentBody,
        buttonText: form.heroButtonText || '',
        openRolesButton: form.heroRoles,
        jobListHeadingText: form.jobListHeadingText,
        shopifyToken,
      },
    };

    if (isSubscribedUser) {
      const careerPortalInput = {
        introductionParagraph: form.introductionParagraph,
        introductionBody: form.introductionBody,
        introductionHeading: form.introductionHeading,
        employeeTestimonial: form.employeeTestimonial,
        departmentsSection: form.departmentsSection,
      };

      payload.variables = {
        ...payload.variables,
        ...reject(isNil)(careerPortalInput),
      };
    }

    if (isStylesHeroEnabled) {
      const heroSection = {
        enabled: true,
        backgroundColor: backgroundHeroColor,
        headingColor: headingHeroColor,
        subheadingColor: subheadingHeroColor,
      };

      payload.variables = {
        ...payload.variables,
        heroSection,
      };
    } else {
      payload.variables = {
        ...payload.variables,
        heroSection: {
          enabled: false,
        },
      };
    }

    if (isStylesIntroEnabled) {
      const paragraphSection = {
        enabled: true,
        backgroundColor: backgroundIntroColor,
        headingColor: headingIntroColor,
        bodyColor: bodyIntroColor,
      };

      payload.variables = {
        ...payload.variables,
        paragraphSection,
      };
    } else {
      payload.variables = {
        ...payload.variables,
        paragraphSection: {
          enabled: false,
        },
      };
    }

    if (isStylesEmployeeEnabled) {
      const testimonialSection = {
        enabled: true,
        backgroundColor: backgroundEmployeeColor,
        employeeColor: employeeNameTextColor,
        jobTitleColor: jobTitleEmployeeTextColor,
        quoteColor: quoteEmployeeTextColor,
      };

      payload.variables = {
        ...payload.variables,
        testimonialSection,
      };
    } else {
      payload.variables = {
        ...payload.variables,
        testimonialSection: {
          enabled: false,
        },
      };
    }

    if (isStylesJobListEnabled) {
      const jobListSection = {
        enabled: true,
        backgroundColor: jobListBackgroundColor,
        headingColor: jobListHeadingColor,
        jobListTextColor: jobListTextColor,
        applyButtonColor: jobListButtonColor,
        applyButtonTextColor: jobListButtonTextColor,
        jobListColor: jobListListColor,
      };

      payload.variables = {
        ...payload.variables,
        jobListSection,
      };
    } else {
      payload.variables = {
        ...payload.variables,
        jobListSection: {
          enabled: false,
        },
      };
    }

    if (isStylesDeiEnabled) {
      const deiSection = {
        enabled: true,
        backgroundColor: backgroundDeiColor,
        headingColor: headingDeiColor,
        bodyColor: bodyDeiColor,
      };

      payload.variables = {
        ...payload.variables,
        deiSection,
      };
    } else {
      payload.variables = {
        ...payload.variables,
        deiSection: {
          enabled: false,
        },
      };
    }

    if (isStylesDepartmentEnabled) {
      const departmentSection = {
        enabled: true,
        backgroundColor: backgroundDepartmentColor,
        blockColor: blockDepartmentColor,
        headingColor: headingDepartmentColor,
        departmentColor: departmentNameTextColor,
        descriptionColor: departmentDescriptionTextColor,
      };

      payload.variables = {
        ...payload.variables,
        departmentSection,
      };
    } else {
      payload.variables = {
        ...payload.variables,
        departmentSection: {
          enabled: false,
        },
      };
    }

    let promisesArray = [];

    if (!isEmpty(form.testimonials)) {
      promisesArray = form.testimonials.map(({ isNew, id, ...variables }) => {
        if (isNew) {
          return createTestimonial({ variables: { ...variables, shopifyToken } });
        }
        const updated = {
          testimonialId: id,
          ...variables,
          testimonialPhoto: variables.testimonialPhoto?.name ? variables.testimonialPhoto : null,
          shopifyToken,
        };
        return updateTestimonial({
          variables: updated,
        });
      });
    }

    if (!isEmpty(form.departments)) {
      promisesArray = form.departments.map(({ isNew, id, departmentName, departmentDescription }) => {
        if (isNew) {
          return createDepartment({
            variables: {
              headingText: departmentName,
              subheadingText: departmentDescription,
              shopifyToken,
            },
          });
        }
        return updateDepartment({
          variables: {
            departmentId: id,
            headingText: departmentName,
            subheadingText: departmentDescription,
            shopifyToken,
          },
        });
      });
    }

    if (isCareerPortalEmpty) {
      Promise.all([...promisesArray, createShopCareerPortal(payload)])
        .then(onSuccess)
        .catch(onError);
    } else {
      Promise.all([...promisesArray, updateShopCareerPortal(payload)])
        .then(onSuccess)
        .catch(onError);
    }
  };

  const renderPreviewPhoto = () => {
    if (!!file) return window.URL.createObjectURL(file);
    if (selectedTestimonial?.testimonialPhoto?.name) {
      return window.URL.createObjectURL(selectedTestimonial.testimonialPhoto);
    }
    if (selectedTestimonial?.testimonialPhoto) return selectedTestimonial?.testimonialPhoto;
    return uploadImageUrl;
  };

  const handleHeroTabChange = useCallback((selectedTabIndex) => setSelectedHeroTab(selectedTabIndex), []);

  const handleIntroTabChange = useCallback((selectedTabIndex) => setSelectedIntroTab(selectedTabIndex), []);

  const handleEmployeeTabChange = useCallback((selectedTabIndex) => setSelectedEmployeeTab(selectedTabIndex), []);

  const handleJobListingTabChange = useCallback((selectedTabIndex) => setSelectedJobListingTab(selectedTabIndex), []);

  const handleDepartmentTabChange = useCallback((selectedTabIndex) => setSelectedDepartmentTab(selectedTabIndex), []);

  const handleDeiTabChange = useCallback((selectedTabIndex) => setSelectedDeiTab(selectedTabIndex), []);

  const handleChange = (type) => (updatedColor, event) => {
    const hslColor = {
      hue: updatedColor?.hsl?.h,
      brightness: updatedColor?.hsl?.l,
      saturation: updatedColor?.hsl?.s,
      alpha: updatedColor?.hsl?.a,
    };
    event.persist();

    setHasColorChanged(true);
    if (type === 'heroBackground') setBackgroundHeroColor(hslColor);
    if (type === 'heroHeading') setHeadingHeroColor(hslColor);
    if (type === 'heroSubheading') setSubheadingHeroColor(hslColor);
    if (type === 'introBackground') setBackgroundIntroColor(hslColor);
    if (type === 'introHeading') setHeadingIntroColor(hslColor);
    if (type === 'introBodyText') setBodyIntroColor(hslColor);
    if (type === 'employeeBackground') setBackgroundEmployeeColor(hslColor);
    if (type === 'employeeNameText') setEmployeeNameTextColor(hslColor);
    if (type === 'employeeJobTitleText') setJobTitleEmployeeTextColor(hslColor);
    if (type === 'employeeQuoteText') setQuoteEmployeeTextColor(hslColor);
    if (type === 'deiBackground') setBackgroundDeiColor(hslColor);
    if (type === 'deiHeading') setHeadingDeiColor(hslColor);
    if (type === 'deiBodyText') setBodyDeiColor(hslColor);
    if (type === 'departmentBackground') setBackgroundDepartmentColor(hslColor);
    if (type === 'departmentBlock') setBlockDepartmentColor(hslColor);
    if (type === 'departmentHeading') setHeadingDepartmentColor(hslColor);
    if (type === 'departmentNameText') setDepartmentNameTextColor(hslColor);
    if (type === 'departmentDescriptionText') setDepartmentDescriptionTextColor(hslColor);

    if (type === 'joblistBackground') setJobListBackgroundColor(hslColor);
    if (type === 'joblistText') setJobListTextColor(hslColor);
    if (type === 'joblistList') setJobListListColor(hslColor);
    if (type === 'joblistHeading') setJobListHeadingColor(hslColor);
    if (type === 'joblistButton') setJobListButtonColor(hslColor);
    if (type === 'joblistButtonText') setJobListButtonTextColor(hslColor);
  };

  if (!isLoading) {
    return (
      <AppProvider
        i18n={{
          Polaris: {
            ContextualSaveBar: {
              save: 'Save',
              discard: 'Discard',
            },
            DropZone: {
              actionTitle: 'Add image',
            },
          },
        }}>
        <Frame>
          {contextualSaveBarMarkup(createShopCareerPortal, updateShopCareerPortal)}
          <Page
            title='Career Portal Setup'
            subtitle={
              <Button
                external
                plain
                monochrome
                outline={false}
                icon={ViewMajorMonotone}
                url={careerPageLink}>
                View career portal
              </Button>
            }>
            <Form
              onSubmit={(e) => {
                const heroRoles = compose((value) => value, prop('heroRoles'))(form);
                const updatedErrors = {
                  heroHeader: compose(isEmpty, prop('heroHeader'))(form),
                  heroSubheader: compose(isEmpty, prop('heroSubheader'))(form),
                  heroButtonText: heroRoles ? compose(isEmpty, prop('heroButtonText'))(form) : false,
                };

                setFormErrors(updatedErrors);

                if (
                  compose(
                    find((item) => item),
                    values
                  )(updatedErrors)
                ) {
                  setTimeout(() => {
                    onSubmitFailed();
                  }, 10);
                } else {
                  e.preventDefault();
                  handleFormSubmit({ createShopCareerPortal, updateShopCareerPortal });
                }
              }}>
              <Layout>
                <Layout.AnnotatedSection
                  title='Hero'
                  description='The hero section represents the top portion of your career portal pages. Make it bold, engaging and representative of your brand.'>
                  <Card sectioned>
                    <Tabs tabs={heroTabs} selected={selectedHeroTab} onSelect={handleHeroTabChange}>
                      <Card.Section>
                        {!selectedHeroTab ? (
                          <FormLayout>
                            <div>
                              <TextField
                                id='heroHeader'
                                label='Hero header text'
                                value={form.heroHeader}
                                name='heroHeader'
                                onChange={updateField('heroHeader')}
                                placeholder='View Our Open Roles'
                              />
                              {formErrors.heroHeader && <InlineError message='Header is required' fieldID='heroHeader' />}
                            </div>
                            <div>
                              <TextField
                                id='heroSubheader'
                                label='Hero sub-header text'
                                value={form.heroSubheader}
                                name='heroSubheader'
                                onChange={updateField('heroSubheader')}
                                placeholder='We’re looking for great people to join our growing team'
                              />
                              {formErrors.heroSubheader && (
                                <InlineError message='Subheader is required' fieldID='heroSubheader' />
                              )}
                            </div>
                            <div>
                              <Checkbox
                                name='heroRoles'
                                id='heroRoles'
                                label='Include View Open Roles button'
                                ariaControls='basic-collapsible'
                                checked={form.heroRoles}
                                onChange={updateField('heroRoles')}
                                disabled={!isPremiumUser}
                              />
                              <p style={{ color: '#919eab', paddingLeft: '2.5rem', fontSize: '1.2rem' }}>
                                Selecting this button focuses the users view to the job listings section
                              </p>
                            </div>
                            <div>
                              <TextField
                                id='heroButtonText'
                                label='Button text'
                                value={form.heroButtonText}
                                name='heroButtonText'
                                disabled={!isPremiumUser}
                                onChange={updateField('heroButtonText')}
                                disabled={!isPremiumUser}
                              />
                              {formErrors.heroButtonText && (
                                <InlineError message='Button text is required' fieldID='heroButtonText' />
                              )}
                            </div>
                          </FormLayout>
                        ) : (
                          selectedHeroTab && (
                            <ColorPickerCard
                              toggleIsStylesEnabled={toggleIsHeroStylesEnabled}
                              isStylesEnabled={isStylesHeroEnabled}
                              handleChange={handleChange}
                              firstColumnColor={backgroundHeroColor}
                              secondColumnColor={headingHeroColor}
                              thirdColumnColor={subheadingHeroColor}
                              firstColumnName='heroBackground'
                              secondColumnName='heroHeading'
                              thirdColumnName='heroSubheading'
                            />
                          )
                        )}
                      </Card.Section>
                    </Tabs>
                  </Card>
                </Layout.AnnotatedSection>
                <Layout.AnnotatedSection
                  title='Introduction paragraph'
                  description={
                    <>
                      <p style={{ marginBottom: '10px' }}>
                        Let everyone know what your company is all about with a couple sentences.
                      </p>
                      {!isSubscribedUser && (
                        <Link onClick={() => changeRoute('/account-plan')} removeUnderline>
                          Upgrade account plan
                        </Link>
                      )}
                    </>
                  }>
                  <Card sectioned>
                    <Tabs tabs={introTabs} selected={selectedIntroTab} onSelect={handleIntroTabChange}>
                      <Card.Section>
                        {!selectedIntroTab ? (
                          <>
                            {isSubscribedUser ? (
                              <FormLayout>
                                <div style={{ position: 'relative' }}>
                                  <div style={{ position: 'absolute', top: '-12px', right: '0' }}>
                                    <Checkbox
                                      name='introductionParagraph'
                                      id='introductionParagraph'
                                      label='Include section'
                                      ariaControls='basic-collapsible'
                                      checked={form.introductionParagraph}
                                      onChange={updateField('introductionParagraph')}
                                      disabled={!isSubscribedUser}
                                    />
                                  </div>
                                  <TextField
                                    id='introductionHeading'
                                    label='Section Heading'
                                    value={form.introductionHeading}
                                    name='introductionHeading'
                                    onChange={updateField('introductionHeading')}
                                    disabled={!isSubscribedUser}
                                  />
                                  <br />
                                  <TextField
                                    id='introductionBody'
                                    label='Body'
                                    value={form.introductionBody}
                                    name='introductionBody'
                                    multiline={5}
                                    onChange={updateField('introductionBody')}
                                    disabled={!isSubscribedUser}
                                  />
                                </div>
                              </FormLayout>
                            ) : (
                              <Tooltip
                                active={false}
                                content='Please upgrade your account plan to add this section to your careers page.'>
                                <FormLayout>
                                  <div style={{ position: 'relative' }}>
                                    <div style={{ position: 'absolute', top: '-12px', right: '0' }}>
                                      <Checkbox
                                        name='introductionParagraph'
                                        id='introductionParagraph'
                                        label='Include section'
                                        ariaControls='basic-collapsible'
                                        checked={form.introductionParagraph}
                                        onChange={updateField('introductionParagraph')}
                                        disabled={!isSubscribedUser}
                                      />
                                    </div>
                                    <TextField
                                      id='introductionHeading'
                                      label={<span className='is-disabled'>Section Heading</span>}
                                      value={form.introductionHeading}
                                      name='introductionHeading'
                                      onChange={updateField('introductionHeading')}
                                      disabled={!isSubscribedUser}
                                    />
                                    <br />
                                    <TextField
                                      id='introductionBody'
                                      label={<span className='is-disabled'>Body</span>}
                                      value={form.introductionBody}
                                      name='introductionBody'
                                      multiline={5}
                                      onChange={updateField('introductionBody')}
                                      disabled={!isSubscribedUser}
                                    />
                                  </div>
                                </FormLayout>
                              </Tooltip>
                            )}
                          </>
                        ) : (
                          selectedIntroTab && (
                            <ColorPickerCard
                              toggleIsStylesEnabled={toggleIsIntroStylesEnabled}
                              isStylesEnabled={isStylesIntroEnabled}
                              handleChange={handleChange}
                              firstColumnColor={backgroundIntroColor}
                              secondColumnColor={headingIntroColor}
                              thirdColumnColor={bodyIntroColor}
                              firstColumnName='introBackground'
                              secondColumnName='introHeading'
                              thirdColumnName='introBodyText'
                            />
                          )
                        )}
                      </Card.Section>
                    </Tabs>
                  </Card>
                </Layout.AnnotatedSection>
                <Layout.AnnotatedSection
                  title='Employee testimonial'
                  description={
                    <>
                      <p style={{ marginBottom: '10px' }}>Share some nice things employees have to say about your company.</p>
                      {!isSubscribedUser && (
                        <Link onClick={() => changeRoute('/account-plan')} removeUnderline>
                          Upgrade account plan
                        </Link>
                      )}
                    </>
                  }>
                  <Card sectioned>
                    <Tabs tabs={employeeTabs} selected={selectedEmployeeTab} onSelect={handleEmployeeTabChange}>
                      <Card.Section>
                        {!selectedEmployeeTab ? (
                          <div style={{ position: 'relative' }}>
                            <div style={{ position: 'absolute', top: '-12px', right: '0' }}>
                              <Checkbox
                                name='employeeTestimonial'
                                id='employeeTestimonial'
                                label='Include section'
                                ariaControls='basic-collapsible'
                                checked={form.employeeTestimonial}
                                onChange={updateField('employeeTestimonial')}
                                disabled={!isSubscribedUser}
                              />
                            </div>
                            <br />
                            <ResourceList
                              items={form.testimonials || []}
                              renderItem={(item) => {
                                const { id, testimonialPhoto, name, role, quote } = item;
                                return (
                                  <ResourceItem
                                    id={id}
                                    shortcutActions={[
                                      {
                                        content: 'Edit',
                                        onAction: () => {
                                          setSelectedTestimonial(item);
                                          setState({
                                            ...form,
                                            employeeTestimonialName: item.name,
                                            employeeTestimonialQuote: item.quote,
                                            employeeTestimonialRole: item.role,
                                          });
                                          toggleIsTestimonialModalOpen();
                                        },
                                        disabled: !isSubscribedUser,
                                      },
                                      {
                                        content: 'Remove',
                                        onAction: () => {
                                          toggleIsConfirmDeleteModalOpen();
                                          setSelectedTestimonial(item);
                                        },
                                        disabled: !isSubscribedUser,
                                      },
                                    ]}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                      <img
                                        style={{ maxWidth: '70px', marginRight: '15px' }}
                                        src={
                                          testimonialPhoto?.name ? window.URL.createObjectURL(testimonialPhoto) : testimonialPhoto
                                        }
                                        alt='Employee Photo'
                                      />
                                      <div style={{ maxWidth: '300px' }}>
                                        <h3>
                                          <TextStyle variation='strong'>{name}</TextStyle>
                                        </h3>
                                        <div>{role}</div>
                                        <div>{quote}</div>
                                      </div>
                                    </div>
                                  </ResourceItem>
                                );
                              }}
                            />
                            {(form.testimonials?.length < subscriptionPlan?.employee_testimonial ||
                              subscriptionPlan?.employee_testimonial === 'unlimited') && (
                              <div style={{ color: '#637381', display: 'inline-flex', alignItems: 'center', marginTop: '15px' }}>
                                <Icon source={CirclePlusMinor} />
                                {isSubscribedUser ? (
                                  <Button plain monochrome onClick={toggleIsTestimonialModalOpen}>
                                    <span style={{ marginLeft: '10px' }}>
                                      Add Testimonial{' '}
                                      {subscriptionPlan?.employee_testimonial !== 'unlimited' &&
                                        `(maximum of ${subscriptionPlan?.employee_testimonial})`}
                                    </span>
                                  </Button>
                                ) : (
                                  <Tooltip
                                    active={false}
                                    content='Please upgrade your account plan to add this section to your careers page.'>
                                    <Button plain monochrome disabled={!isSubscribedUser} onClick={toggleIsTestimonialModalOpen}>
                                      <span style={{ marginLeft: '10px' }}>
                                        Add Testimonial{' '}
                                        {subscriptionPlan?.employee_testimonial !== 'unlimited' &&
                                          `(maximum of ${subscriptionPlan?.employee_testimonial})`}
                                      </span>
                                    </Button>
                                  </Tooltip>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          selectedEmployeeTab && (
                            <ColorPickerCard
                              toggleIsStylesEnabled={toggleIsEmployeeStylesEnabled}
                              isStylesEnabled={isStylesEmployeeEnabled}
                              handleChange={handleChange}
                              firstColumnColor={backgroundEmployeeColor}
                              secondColumnColor={employeeNameTextColor}
                              thirdColumnColor={jobTitleEmployeeTextColor}
                              fourthColumnColor={quoteEmployeeTextColor}
                              firstColumnName='employeeBackground'
                              secondColumnName='employeeNameText'
                              thirdColumnName='employeeJobTitleText'
                              fourthColumnName='employeeQuoteText'
                            />
                          )
                        )}
                      </Card.Section>
                    </Tabs>
                  </Card>
                </Layout.AnnotatedSection>
                <Layout.AnnotatedSection title='Diversity, Equity and Inclusion Statement'>
                  <Card sectioned>
                    <Tabs tabs={deiTabs} selected={selectedDeiTab} onSelect={handleDeiTabChange}>
                      <Card.Section>
                        {!selectedDeiTab ? (
                          <FormLayout>
                            <div style={{ position: 'relative' }}>
                              <div style={{ position: 'absolute', top: '-12px', right: '0' }}>
                                <Checkbox
                                  name='deiStatment'
                                  id='deiStatment'
                                  label='Include section'
                                  ariaControls='basic-collapsible'
                                  checked={form.deiStatment}
                                  onChange={updateField('deiStatment')}
                                />
                              </div>
                              <TextField
                                id='deiStatmentBody'
                                label='DEI Statement'
                                value={form.deiStatmentBody}
                                name='deiStatmentBody'
                                multiline={5}
                                onChange={updateField('deiStatmentBody')}
                              />
                            </div>
                          </FormLayout>
                        ) : (
                          selectedDeiTab && (
                            <ColorPickerCard
                              toggleIsStylesEnabled={toggleIsDeiStylesEnabled}
                              isStylesEnabled={isStylesDeiEnabled}
                              handleChange={handleChange}
                              firstColumnColor={backgroundDeiColor}
                              secondColumnColor={headingDeiColor}
                              thirdColumnColor={bodyDeiColor}
                              firstColumnName='deiBackground'
                              secondColumnName='deiHeading'
                              thirdColumnName='deiBodyText'
                            />
                          )
                        )}
                      </Card.Section>
                    </Tabs>
                  </Card>
                </Layout.AnnotatedSection>

                {/* <Layout.AnnotatedSection
                  title='Departments'
                  description={
                    <>
                      <p style={{ marginBottom: '10px' }}>Add the departments of your company.</p>
                      {!isPremiumUser && <Link onClick={() => changeRoute('/account-plan')} removeUnderline>Upgrade account plan</Link>}
                    </>
                  }>
                  <Card sectioned>
                    <Tabs tabs={departmentTabs} selected={selectedDepartmentTab} onSelect={handleDepartmentTabChange}>
                      <Card.Section>
                        {!selectedDepartmentTab ? (
                          <div style={{ position: 'relative' }}>
                            <div style={{ position: 'absolute', top: '-12px', right: '0' }}>
                              <Checkbox
                                name='departmentsSection'
                                id='departmentsSection'
                                label='Include section'
                                ariaControls='basic-collapsible'
                                checked={form.departmentsSection}
                                onChange={updateField('departmentsSection')}
                                disabled={!isPremiumUser}
                              />
                            </div>
                            <br />
                            <ResourceList
                              items={form.departments || []}
                              renderItem={(item) => {
                                const { id, departmentName, departmentDescription } = item;
                                return (
                                  <ResourceItem
                                    id={id}
                                    shortcutActions={[
                                      {
                                        content: 'Edit',
                                        onAction: () => {
                                          setSelectedDepartment({
                                            id,
                                            departmentName,
                                            departmentDescription,
                                          });
                                          setState({
                                            ...form,
                                            departmentName,
                                            departmentDescription,
                                          });
                                          toggleIsDepartmentsModalOpen();
                                        },
                                        disabled: !isPremiumUser,
                                      },
                                      {
                                        content: 'Remove',
                                        onAction: () => {
                                          toggleIsConfirmDeleteDepartmentModalOpen();
                                          setSelectedDepartment(item);
                                        },
                                        disabled: !isSubscribedUser,
                                      },
                                    ]}>
                                    <div style={{ padding: '10px 5px' }}>
                                      <h3>
                                        <TextStyle variation='strong'>{departmentName}</TextStyle>
                                      </h3>
                                      <div>{departmentDescription}</div>
                                    </div>
                                  </ResourceItem>
                                );
                              }}
                            />
                            {form.departments?.length < 9 && (
                              <div style={{ color: '#637381', display: 'inline-flex', alignItems: 'center', marginTop: '15px' }}>
                                <Icon source={CirclePlusMinor} />
                                {isPremiumUser ? (
                                  <Button plain monochrome onClick={toggleIsDepartmentsModalOpen}>
                                    <span style={{ marginLeft: '10px' }}>Add Department (maximum of 9)</span>
                                  </Button>
                                ) : (
                                  <Tooltip
                                    active={false}
                                    content='Please upgrade your account plan to add this section to your careers page.'>
                                    <Button plain monochrome disabled={!isPremiumUser} onClick={toggleIsDepartmentsModalOpen}>
                                      <span style={{ marginLeft: '10px' }}>Add Department (maximum of 9)</span>
                                    </Button>
                                  </Tooltip>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          selectedDepartmentTab && (
                            <ColorPickerCard
                              toggleIsStylesEnabled={toggleIsDepartmentStylesEnabled}
                              isStylesEnabled={isStylesDepartmentEnabled}
                              handleChange={handleChange}
                              firstColumnColor={backgroundDepartmentColor}
                              secondColumnColor={blockDepartmentColor}
                              thirdColumnColor={headingDepartmentColor}
                              fourthColumnColor={departmentNameTextColor}
                              fifthColumnColor={departmentDescriptionTextColor}
                              firstColumnName='departmentBackground'
                              secondColumnName='departmentBlock'
                              thirdColumnName='departmentHeading'
                              fourthColumnName='departmentNameText'
                              fifthColumnName='departmentDescriptionText'
                            />
                          )
                        )}
                      </Card.Section>
                    </Tabs>
                  </Card>
                </Layout.AnnotatedSection>
                */}

                <Layout.AnnotatedSection
                  title='Job listings'
                  description={
                    <>
                      <p style={{ marginBottom: '10px' }}>Customize the job listings section of your careers page.</p>
                    </>
                  }>
                  <Card sectioned>
                    <Tabs tabs={jobListingTabs} selected={selectedJobListingTab} onSelect={handleJobListingTabChange}>
                      <Card.Section>
                        {!selectedJobListingTab ? (
                          <div style={{ position: 'relative' }}>
                            <TextField
                              id='jobListHeadingText'
                              label='Section Heading'
                              value={form.jobListHeadingText}
                              name='jobListHeadingText'
                              onChange={updateField('jobListHeadingText')}
                            />
                          </div>
                        ) : (
                          selectedJobListingTab && (
                            <ColorPickerCard
                              toggleIsStylesEnabled={toggleIsJobListStylesEnabled}
                              isStylesEnabled={isStylesJobListEnabled}
                              handleChange={handleChange}
                              firstColumnColor={jobListBackgroundColor}
                              secondColumnColor={jobListTextColor}
                              thirdColumnColor={jobListListColor}
                              fourthColumnColor={jobListHeadingColor}
                              fifthColumnColor={jobListButtonColor}
                              sixthColumnColor={jobListButtonTextColor}
                              firstColumnName='joblistBackground'
                              secondColumnName='joblistText'
                              thirdColumnName='joblistList'
                              fourthColumnName='joblistHeading'
                              fifthColumnName='joblistButton'
                              sixthColumnName='joblistButtonText'
                            />
                          )
                        )}
                      </Card.Section>
                    </Tabs>
                  </Card>
                </Layout.AnnotatedSection>

                <Layout.AnnotatedSection title='SEO' description='SEO description goes here.'>
                  <Card sectioned>
                    <FormLayout>
                      <TextField
                        id='pageTitle'
                        label='Page title'
                        value={form.pageTitle}
                        name='pageTitle'
                        onChange={updateField('pageTitle')}
                        placeholder='Careers | [store name]'
                      />
                      <TextField
                        id='metaDescription'
                        label='Meta description'
                        value={form.metaDescription}
                        name='metaDescription'
                        multiline={5}
                        onChange={updateField('metaDescription')}
                      />
                      <Label>URL and Handle</Label>
                      <Stack alignment='center'>
                        <Stack.Item fill>
                          <TextField
                            id='urlAndHandle'
                            label=''
                            value={form.urlAndHandle}
                            name='urlAndHandle'
                            onChange={updateField('urlAndHandle')}
                            placeholder={careerPageLink}
                            disabled
                          />
                        </Stack.Item>
                        <Stack.Item>
                          <Button
                            size='slim'
                            onClick={() => {
                              navigator.clipboard.writeText(careerPageLink);
                              togglePasted();
                            }}>
                            Copy URL
                          </Button>
                        </Stack.Item>
                      </Stack>
                    </FormLayout>
                  </Card>
                </Layout.AnnotatedSection>
              </Layout>
              <br />
              <div className='submitButton'>
                <Button primary={true} submit={true} size='large'>
                  {actionButtonText}
                </Button>
              </div>
              {pasted ? <Toast content='Copied' duration={1000} onDismiss={togglePasted} /> : null}
            </Form>
          </Page>
          {toastMarkup}
        </Frame>
        <Modal
          open={isTestimonialModalOpen}
          onClose={toggleIsTestimonialModalOpen}
          title={selectedTestimonial?.id ? 'Update Testimonial' : 'Add Testimonial'}
          primaryAction={{
            content: selectedTestimonial?.id ? 'Update Testimonial' : 'Add Testimonial',
            onAction: () => {
              const updatedTestimonial = {
                name: form.employeeTestimonialName,
                quote: form.employeeTestimonialQuote,
                role: form.employeeTestimonialRole,
                testimonialPhoto: file || null,
                shopifyToken,
              };

              let updatedTestimonials = [...form.testimonials];
              const newTestimonialId = `new-${updatedTestimonials.length + 1}-${updatedTestimonial.name}`;
              if (!selectedTestimonial?.id) {
                updatedTestimonials.push({ id: newTestimonialId, ...updatedTestimonial, isNew: true });
              } else {
                updatedTestimonials = updatedTestimonials.map((testimonial) => {
                  if (testimonial.id === selectedTestimonial?.id)
                    return {
                      ...testimonial,
                      ...updatedTestimonial,
                      testimonialPhoto: updatedTestimonial.testimonialPhoto || testimonial.testimonialPhoto,
                    };
                  return testimonial;
                });
              }

              setState({
                ...form,
                employeeTestimonialName: '',
                employeeTestimonialQuote: '',
                employeeTestimonialRole: '',
                testimonials: updatedTestimonials,
              });
              setFile(null);
              toggleIsTestimonialModalOpen();
              setSelectedTestimonial(null);
            },
            disabled:
              (!file && !selectedTestimonial?.id) ||
              !form.employeeTestimonialName ||
              !form.employeeTestimonialQuote ||
              !form.employeeTestimonialRole ||
              isLoading,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => {
                setState({
                  ...form,
                  employeeTestimonialName: '',
                  employeeTestimonialQuote: '',
                  employeeTestimonialRole: '',
                });
                setSelectedTestimonial(null);
                toggleIsTestimonialModalOpen();
              },
            },
          ]}
          loading={false}>
          <Modal.Section>
            <FormLayout>
              <FormLayout.Group>
                <DropZone allowMultiple={false} onDrop={handleDropZoneDrop} disabled={!isSubscribedUser}>
                  {uploadedFile}
                  {fileUpload}
                </DropZone>
                <div>
                  <Subheading>Preview</Subheading>
                  <br />
                  <Card>
                    <Card.Section>
                      <img style={{ width: '70px' }} src={renderPreviewPhoto()} />
                    </Card.Section>
                  </Card>
                </div>
              </FormLayout.Group>
            </FormLayout>
            <br />
            <FormLayout>
              <TextField
                id='employeeTestimonialName'
                label='Employee name'
                value={form.employeeTestimonialName}
                name='employeeTestimonialName'
                onChange={updateField('employeeTestimonialName')}
                disabled={!isSubscribedUser}
              />
              <TextField
                id='employeeTestimonialRole'
                label='Job Title'
                value={form.employeeTestimonialRole}
                name='employeeTestimonialRole'
                onChange={updateField('employeeTestimonialRole')}
                disabled={!isSubscribedUser}
              />
              <TextField
                id='employeeTestimonialQuote'
                label='Quote'
                value={form.employeeTestimonialQuote}
                name='employeeTestimonialQuote'
                multiline={5}
                onChange={updateField('employeeTestimonialQuote')}
                disabled={!isSubscribedUser}
              />
            </FormLayout>
          </Modal.Section>
        </Modal>
        <Modal
          open={isDepartmentsModalOpen}
          onClose={toggleIsDepartmentsModalOpen}
          title={selectedDepartment?.id ? 'Update Department' : 'Add Department'}
          primaryAction={{
            content: selectedDepartment?.id ? 'Update Department' : 'Add Department',
            onAction: () => {
              const updatedDepartment = {
                departmentName: form.departmentName,
                departmentDescription: form.departmentDescription,
                shopifyToken,
              };

              let updatedDepartments = [...form.departments];
              const newDepartmentId = `new-${updatedDepartments.length + 1}-${updatedDepartment.name}`;
              if (!selectedDepartment?.id) {
                updatedDepartments.push({ id: newDepartmentId, ...updatedDepartment, isNew: true });
              } else {
                updatedDepartments = updatedDepartments.map((department) => {
                  if (department.id === selectedDepartment?.id)
                    return {
                      ...department,
                      ...updatedDepartment,
                    };
                  return department;
                });
              }

              setState({
                ...form,
                departmentName: '',
                departmentDescription: '',
                departments: updatedDepartments,
              });
              setFile(null);
              toggleIsDepartmentsModalOpen();
              setSelectedTestimonial(null);
            },
            disabled: !form.departmentName || !form.departmentDescription,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => {
                setState({
                  ...form,
                  departmentName: '',
                  departmentDescription: '',
                });
                setSelectedDepartment(null);
                toggleIsDepartmentsModalOpen();
              },
            },
          ]}
          loading={false}>
          <Modal.Section>
            <FormLayout>
              <TextField
                id='departmentName'
                label='Department Name'
                value={form.departmentName}
                name='departmentName'
                onChange={updateField('departmentName')}
                disabled={!isSubscribedUser}
              />
              <TextField
                id='departmentDescription'
                label='Department Description'
                value={form.departmentDescription}
                name='departmentDescription'
                multiline={5}
                onChange={updateField('departmentDescription')}
                disabled={!isSubscribedUser}
              />
            </FormLayout>
          </Modal.Section>
        </Modal>
        <Modal
          open={isConfirmDeleteModalOpen}
          onClose={toggleIsConfirmDeleteModalOpen}
          title='Confirm'
          primaryAction={{
            content: 'Delete',
            onAction: () => {
              if (selectedTestimonial.isNew) {
                setState({
                  ...form,
                  testimonials: form.testimonials.filter(({ id }) => id !== selectedTestimonial.id),
                });
                setSelectedTestimonial(null);
                toggleIsConfirmDeleteModalOpen();
              } else {
                setIsLoading(true);
                deleteTestimonial({
                  variables: {
                    shopifyToken,
                    testimonialId: selectedTestimonial.id,
                  },
                })
                  .then(() => {
                    setState({
                      ...form,
                      testimonials: form.testimonials.filter(({ id }) => id !== selectedTestimonial.id),
                    });
                    toggleIsConfirmDeleteModalOpen();
                    toggleToast('The testimonial was deleted successfully!');
                    setIsLoading(false);
                    setSelectedTestimonial(null);
                  })
                  .catch(() => {
                    setIsLoading(false);
                    toggleToast('There was a problem with your request');
                  });
              }
            },
            disabled: isLoading,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: toggleIsConfirmDeleteModalOpen,
            },
          ]}
          loading={false}>
          <Modal.Section>
            <p>Are you sure you want to delete this testimonial?</p>
          </Modal.Section>
        </Modal>
        <Modal
          open={isConfirmDeleteDepartmentModalOpen}
          onClose={toggleIsConfirmDeleteDepartmentModalOpen}
          title='Confirm'
          primaryAction={{
            content: 'Delete',
            onAction: () => {
              if (selectedDepartment.isNew) {
                setState({
                  ...form,
                  departments: form.departments.filter(({ id }) => id !== selectedDepartment.id),
                });
                setSelectedDepartment(null);
                toggleIsConfirmDeleteDepartmentModalOpen();
              } else {
                setIsLoading(true);
                deleteDepartment({
                  variables: {
                    shopifyToken,
                    departmentId: selectedDepartment.id,
                  },
                })
                  .then(() => {
                    setState({
                      ...form,
                      departments: form.departments.filter(({ id }) => id !== selectedDepartment.id),
                    });
                    toggleIsConfirmDeleteDepartmentModalOpen();
                    toggleToast('The department was deleted successfully!');
                    setIsLoading(false);
                    setSelectedDepartment(null);
                  })
                  .catch(() => {
                    setIsLoading(false);
                    toggleToast('There was a problem with your request');
                  });
              }
            },
            disabled: isLoading,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: toggleIsConfirmDeleteDepartmentModalOpen,
            },
          ]}
          loading={false}>
          <Modal.Section>
            <p>Are you sure you want to delete this department?</p>
          </Modal.Section>
        </Modal>
      </AppProvider>
    );
  } else {
    return (
      <AppProvider>
        <div
          style={{
            width: '100%',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
        </div>
      </AppProvider>
    );
  }
};

export default CareerPortal;
